/**
 * Per-domain catalog install tables (Task #1991).
 *
 * One table per catalog domain that previously fell back to the
 * `tenants.settings.catalogConfig.*` JSONB bucket. Every table has the
 * same uniform shape so downstream domain code can read its catalog
 * installs by name and pack-service can apply / rollback them
 * idempotently:
 *
 *   id            uuid pk
 *   tenant_id     text not null   -- owning tenant
 *   item_key      text not null   -- catalog item key (unique per tenant)
 *   display_name  text not null
 *   description   text
 *   payload       jsonb not null  -- merged catalog payload + override
 *   provenance    text not null   -- "catalog:<domain>/<slug>:<itemKey>"
 *   created_at / updated_at
 *   UNIQUE (tenant_id, item_key)
 *
 * Apply is `INSERT … ON CONFLICT (tenant_id, item_key) DO UPDATE`,
 * rollback is `DELETE WHERE tenant_id = $1 AND provenance = $2`.
 */
import { sql } from "drizzle-orm";
import {
  pgTable,
  uuid,
  text,
  timestamp,
  jsonb,
  unique,
  index,
} from "drizzle-orm/pg-core";

function defineInstallTable(tableName: string) {
  return pgTable(
    tableName,
    {
      id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
      tenantId: text("tenant_id").notNull(),
      itemKey: text("item_key").notNull(),
      displayName: text("display_name").notNull(),
      description: text("description"),
      payload: jsonb("payload")
        .$type<Record<string, unknown>>()
        .notNull()
        .default(sql`'{}'::jsonb`),
      provenance: text("provenance").notNull(),
      createdAt: timestamp("created_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
      updatedAt: timestamp("updated_at", { withTimezone: true })
        .notNull()
        .defaultNow(),
    },
    (t) => ({
      uniqByTenantItem: unique(`${tableName}_tenant_item_uniq`).on(
        t.tenantId,
        t.itemKey,
      ),
      tenantIdx: index(`${tableName}_tenant_idx`).on(t.tenantId),
    }),
  );
}

export const mobileAlertTiers = defineInstallTable("mobile_alert_tiers");
export const helpTourDefinitions = defineInstallTable("help_tour_definitions");
export const observabilityDashboardTemplates = defineInstallTable(
  "observability_dashboard_templates",
);
export const reportingDashboardTemplates = defineInstallTable(
  "reporting_dashboard_templates",
);
export const workflowBlueprintTemplates = defineInstallTable(
  "workflow_blueprint_templates",
);
export const sitesTopologyTemplates = defineInstallTable(
  "sites_topology_templates",
);
export const embedOgCardTemplates = defineInstallTable(
  "embed_og_card_templates",
);
export const ptwPresetPacks = defineInstallTable("ptw_preset_packs");
export const ptwRecipeTemplates = defineInstallTable("ptw_recipe_templates");
export const walletQrConfigs = defineInstallTable("wallet_qr_configs");
export const printOfflineConfigs = defineInstallTable("print_offline_configs");
export const ltirRegisterTemplates = defineInstallTable(
  "ltir_register_templates",
);
export const sharingSpaceTemplates = defineInstallTable(
  "sharing_space_templates",
);
export const integrationConnectorTemplates = defineInstallTable(
  "integration_connector_templates",
);
export const workforceTradeTemplates = defineInstallTable(
  "workforce_trade_templates",
);
